import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Heart, Wallet } from 'lucide-react';

/**
 * Read-only + pay action for a barber's tips.
 * Props:
 *  - data: response payload from GET /api/barbers/{id}/tips (or null when closed)
 *  - onClose(): parent state setter to null
 *  - onPayTips(barberId): handler to mark pending tips as paid
 *  - paying: boolean while pay action is in flight
 */
export default function BarberTipsDialog({ data, onClose, onPayTips, paying }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Propinas de {data?.barber_name}
          </DialogTitle>
        </DialogHeader>
        {data && (
          <div className="space-y-4 mt-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-sm">
                <p className="text-xs text-pink-500 uppercase">Pendientes</p>
                <p className="text-2xl text-pink-500 font-heading">
                  ${(data.total_pending || 0).toFixed(2)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  {data.pending_tips?.length || 0} propina(s)
                </p>
              </div>
              <div className="p-4 bg-zinc-800 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase">Total Pagado</p>
                <p className="text-2xl text-white font-semibold">
                  ${(data.total_paid || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Pay Button */}
            {data.total_pending > 0 && (
              <Button
                onClick={() => onPayTips(data.barber_id)}
                disabled={paying}
                className="w-full bg-pink-500 text-white hover:bg-pink-600"
              >
                <Wallet className="w-4 h-4 mr-2" />
                {paying ? 'Procesando...' : `Pagar Propinas ($${data.total_pending.toFixed(2)})`}
              </Button>
            )}

            {/* Pending Tips List */}
            {data.pending_tips?.length > 0 && (
              <TipList title="Propinas Pendientes" items={data.pending_tips} amountColor="text-pink-500" />
            )}

            {/* Paid Tips List (top 10) */}
            {data.paid_tips?.length > 0 && (
              <TipList title="Propinas Pagadas" items={data.paid_tips.slice(0, 10)} amountColor="text-zinc-400" faded />
            )}

            {!data.pending_tips?.length && !data.paid_tips?.length && (
              <div className="text-center py-8 text-zinc-500">
                <Heart className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No hay propinas registradas</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TipList({ title, items, amountColor, faded }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">{title}</p>
      <ScrollArea className={items.length > 4 ? 'h-[200px]' : 'h-auto'}>
        <div className="space-y-2">
          {items.map((tip) => (
            <div
              key={tip.sale_id || `${tip.date}-${tip.amount}`}
              className={`flex items-center justify-between p-3 rounded-sm ${faded ? 'bg-zinc-800/30 opacity-60' : 'bg-zinc-800/50'}`}
            >
              <div>
                <p className="text-sm text-white">{tip.branch_name || 'Sucursal'}</p>
                <p className="text-xs text-zinc-500">{tip.date}</p>
              </div>
              <span className={`${amountColor} font-semibold`}>${tip.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
